import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import { API_BASE_URL } from '../config/api';
import './common.css';
import './Logs.css';

function Logs() {
    const [logFiles, setLogFiles] = useState([]);
    const [selectedLog, setSelectedLog] = useState(null);
    const [logContent, setLogContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadingContent, setLoadingContent] = useState(false);
    const [error, setError] = useState(null);
    const [logTypeFilter, setLogTypeFilter] = useState('sync_');
    const navigate = useNavigate();

    const fetchLogFiles = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await fetch(`${API_BASE_URL}/api/logs`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.status === 401) {
                localStorage.removeItem('token');
                navigate('/login');
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to fetch log files');
            }

            const data = await response.json();
            setLogFiles(data.files || []);
        } catch (err) {
            console.error('Error fetching log files:', err);
            setError('Failed to load log files');
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchLogFiles();
    }, [fetchLogFiles]);

    const fetchLogContent = async (filename) => {
        try {
            setLoadingContent(true);
            setError(null);

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/logs/${filename}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.status === 401) {
                localStorage.removeItem('token');
                navigate('/login');
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to fetch log content');
            }

            const data = await response.json();
            setLogContent(data.content || '');
            setSelectedLog(filename);
        } catch (err) {
            console.error('Error fetching log content:', err);
            setError('Failed to load log content');
        } finally {
            setLoadingContent(false);
        }
    };

    const handleLogClick = (filename) => {
        fetchLogContent(filename);
    };

    const handleBackToList = () => {
        setSelectedLog(null);
        setLogContent('');
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    // Extract unique log types from file names (text before first underscore)
    const getLogTypes = () => {
        const types = new Set();
        logFiles.forEach(file => {
            const firstUnderscore = file.name.indexOf('_');
            if (firstUnderscore > 0) {
                const prefix = file.name.substring(0, firstUnderscore + 1);
                types.add(prefix);
            }
        });
        return Array.from(types).sort();
    };

    // Filter log files based on selected type
    const filteredLogFiles = logTypeFilter === 'all' 
        ? logFiles 
        : logFiles.filter(file => file.name.startsWith(logTypeFilter));

    return (
        <div className="page-container">
            <Navigation />
            <div className="page-content">
                <div className="page-header">
                    <h2>Sync Logs</h2>
                    <div className="header-actions">
                        {!selectedLog && logFiles.length > 0 && (
                            <div className="log-filter">
                                <label htmlFor="logTypeFilter">Log Type: </label>
                                <select 
                                    id="logTypeFilter"
                                    className="form-select"
                                    value={logTypeFilter} 
                                    onChange={(e) => setLogTypeFilter(e.target.value)}
                                >
                                    <option value="all">All Logs</option>
                                    {getLogTypes().map(type => (
                                        <option key={type} value={type}>
                                            {type.replace('_', '')}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {selectedLog && (
                            <button className="btn btn-secondary" onClick={handleBackToList}>
                                ← Back to List
                            </button>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="alert alert-error">
                        {error}
                    </div>
                )}

                {!selectedLog ? (
                    <div className="card">
                        {loading ? (
                            <div className="loading">Loading log files...</div>
                        ) : filteredLogFiles.length === 0 ? (
                            <div className="no-logs">
                                {logFiles.length === 0 
                                    ? 'No log files found. Logs will appear here after the sync runs.'
                                    : `No log files found for filter: ${logTypeFilter}`
                                }
                            </div>
                        ) : (
                            <div className="table-container">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>File Name</th>
                                            <th>Date</th>
                                            <th>Size</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredLogFiles.map((file) => (
                                            <tr key={file.name}>
                                                <td className="log-filename">{file.name}</td>
                                                <td>{formatDate(file.modified)}</td>
                                                <td>{formatFileSize(file.size)}</td>
                                                <td>
                                                    <button
                                                        className="btn btn-view"
                                                        onClick={() => handleLogClick(file.name)}
                                                    >
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="log-content">
                        <div className="log-content-header">
                            <h3>{selectedLog}</h3>
                        </div>
                        {loadingContent ? (
                            <div className="loading">Loading log content...</div>
                        ) : (
                            <pre className="log-content-text">{logContent}</pre>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default Logs;
